import SearchIcon from '@mui/icons-material/Search';
import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {useCallback, useState} from 'react';

import PageTitle from '../components/PageTitle';
import Scrollbar from '../components/scrollbar';
import {GeneralLayout} from '../layouts/GeneralLayout';
import {useLazySearchEmployeesWithJobsQuery} from '../store/employee/employeeApi';

export default function EmployeeJobsSearchPage() {
    const [fullName, setFullName] = useState('');
    const [employees, setEmployees] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchEmployeesWithJobs, {isFetching}] = useLazySearchEmployeesWithJobsQuery();

    function formatDate(date) {
        return date ? new Date(date).toLocaleDateString('ru-RU') : '';
    }

    const handleSearch = useCallback(async () => {
        const searchText = fullName.trim();
        if (!searchText) {
            setEmployees([]);
            setHasSearched(false);
            return;
        }

        try {
            const response = await searchEmployeesWithJobs(searchText).unwrap();
            setEmployees(response.data ?? []);
            setHasSearched(true);
        } catch (error) {
            setEmployees([]);
            setHasSearched(false);
            alert('Не удалось выполнить поиск');
        }
    }, [fullName, searchEmployeesWithJobs]);

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    }, [handleSearch]);

    return <GeneralLayout>
        <PageTitle title="Поиск ставок преподавателя"/>

        <Container>
            <Stack direction="column" padding="120px 0px 0px 100px" spacing={2}>
                <Typography variant="h4" gutterBottom>
                    Поиск ставок преподавателя
                </Typography>

                <Card>
                    <CardContent>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="stretch">
                            <TextField
                                label="ФИО преподавателя"
                                value={fullName}
                                onChange={(event) => setFullName(event.target.value)}
                                onKeyDown={handleKeyDown}
                                fullWidth
                            />
                            <Button
                                variant="contained"
                                startIcon={isFetching ? <CircularProgress size={18} color="inherit"/> : <SearchIcon/>}
                                onClick={handleSearch}
                                disabled={isFetching || !fullName.trim()}
                            >
                                Найти
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>

                {hasSearched && <Card>
                    <Scrollbar>
                        <TableContainer sx={{minWidth: 650}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ФИО преподавателя</TableCell>
                                        <TableCell>Кафедра</TableCell>
                                        <TableCell>Ставки</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {employees.map((employee) => <TableRow key={`${employee.fullName}-${employee.chairName}`} hover>
                                        <TableCell>{employee.fullName}</TableCell>
                                        <TableCell>{employee.chairName}</TableCell>
                                        <TableCell>
                                            <Stack component="span" spacing={0.5}>
                                                {employee.jobs.map((job, index) => <Typography component="span" variant="body2" key={`${job.jobDateStart}-${index}`}>
                                                    {formatDate(job.jobDateStart)} — {formatDate(job.jobDateEnd)}
                                                </Typography>)}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>)}
                                    {employees.length === 0 && <TableRow>
                                        <TableCell colSpan={3} align="center">Преподаватели не найдены</TableCell>
                                    </TableRow>}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Scrollbar>
                </Card>}
                
                <br />
            </Stack>
        </Container>
    </GeneralLayout>;
}
